import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { v4 } from 'uuid';
import {
  CheckoutBuilderComponentTypes,
  CheckoutBuilderDeviceTypes,
} from '@/sections/@checkout-builder/constants';
import { defaultCheckoutConfig } from '@/sections/@checkout-builder/constants/defaultCheckoutConfig';

// `coupon` não está nos defaults do builder; sem ele o select do cupom nasce não controlado.
export const EXIT_POPUP_DEFAULTS = {
  ...CheckoutBuilderComponentTypes.exitPopup.attributes,
  coupon: null,
};

// O checkout lê o popup por dispositivo (`config.<device>.extra.exitPopup`), cada um com seu
// `enabled` e seu conteúdo.
export const EXIT_POPUP_DEVICES = Object.keys(CheckoutBuilderDeviceTypes);

export function getExitPopupAttributes(config, device) {
  return { ...EXIT_POPUP_DEFAULTS, ...config?.[device]?.extra?.exitPopup?.attributes };
}

// "Mesma configuração" compara só o conteúdo: cada dispositivo continua ligado ou desligado à parte.
export function hasSameExitPopupContent(a, b) {
  return isEqual(omit(a, 'enabled'), omit(b, 'enabled'));
}

export function withExitPopupContent(source, target) {
  return { ...source, enabled: target?.enabled };
}

export function hasEnabledExitPopup(attributesByDevice) {
  return EXIT_POPUP_DEVICES.some((device) => Boolean(attributesByDevice?.[device]?.enabled));
}

// Sem config próprio, gravar o popup grava junto um layout: o padrão que nunca passou pelo
// builder passa a renderizar pelo builder, e o não padrão deixa de herdar o layout do padrão.
// Com o popup desligado antes e depois, o comprador não veria diferença nenhuma que pague isso,
// então o save não grava, nem o conteúdo digitado. A tela usa a mesma regra para avisar antes.
export function skipsExitPopupSave(checkout, attributesByDevice) {
  const hasOwnConfig = Boolean(checkout?.config) && !checkout?.inheritsDefaultConfig;
  const currentAttributes = Object.fromEntries(
    EXIT_POPUP_DEVICES.map((device) => [device, getExitPopupAttributes(checkout?.config, device)])
  );
  return (
    !hasOwnConfig &&
    !hasEnabledExitPopup(attributesByDevice) &&
    !hasEnabledExitPopup(currentAttributes)
  );
}

export function applyExitPopupToConfig(config, attributesByDevice) {
  // Sem config nem padrão para herdar, o checkout público usa o layout sem builder. Gravar só o
  // `extra` o faria renderizar pelo builder sem `rows`, sem formulário de pagamento; então parte do
  // mesmo layout que o primeiro save do builder gravaria.
  const { desktop, mobile } = defaultCheckoutConfig;
  const base = config?.desktop && config?.mobile ? config : { desktop, mobile, ...config };

  return EXIT_POPUP_DEVICES.reduce(
    (acc, device) => {
      const current = base[device];
      const currentPopup = current?.extra?.exitPopup;

      acc[device] = {
        ...current,
        extra: {
          ...current?.extra,
          exitPopup: {
            ...currentPopup,
            id: currentPopup?.id ?? v4(),
            type: CheckoutBuilderComponentTypes.exitPopup.type,
            attributes: { ...attributesByDevice[device] },
          },
        },
      };

      return acc;
    },
    { ...base }
  );
}

// O builder carrega o config inteiro ao abrir e regrava tudo no save. Sem isso, um builder aberto
// antes de alguém salvar o popup pelo resumo apagaria essa configuração com a cópia velha.
export function keepServerExitPopup(config, serverConfig) {
  return EXIT_POPUP_DEVICES.reduce(
    (acc, device) => {
      const serverPopup = serverConfig?.[device]?.extra?.exitPopup;

      if (serverPopup && acc[device]) {
        acc[device] = { ...acc[device], extra: { ...acc[device].extra, exitPopup: serverPopup } };
      }

      return acc;
    },
    { ...config }
  );
}
